import { CreateContractPartyRequest, PartyResponse, PartyRole } from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { api } from '@/modules/common/api/index.ts';
import PartyModal from '@/modules/admin-dashboard/components/contractManagement/PartyModal.tsx';
import { useCreateContractPartyMutation } from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
} from 'ui';
import { PlusIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

interface ContractPartyModalProps extends BaseModalProps {
  contractId: number;
}

type ContractPartyFormValues = {
  party_id?: number;
  partyOption?: SelectPaginateOption<number> | null;
  role_type: PartyRole;
};

const roleOptions: SelectOption<PartyRole>[] = Object.values(PartyRole).map(role => ({
  value: role,
  label: fixNameEnum(role),
}));

const validationSchema = yup.object<ContractPartyFormValues>({
  party_id: yup.number().required('Party is required'),
  role_type: yup.mixed<PartyRole>().required('Role is required'),
});

const ContractPartyModal = ({ contractId, open, onClose }: ContractPartyModalProps) => {
  const [isPartyModalOpen, setIsPartyModalOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const formik = useFormik<ContractPartyFormValues>({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      party_id: undefined,
      partyOption: null,
      role_type: PartyRole.Customer,
    },
    validationSchema,
    onSubmit: async values => {
      if (!values.party_id) return;

      const payload: CreateContractPartyRequest = {
        contract_id: contractId,
        party_id: values.party_id,
        role_type: values.role_type,
      };

      await createContractParty(payload);
      onClose();
    },
  });

  const { mutateAsync: createContractParty, isLoading } = useCreateContractPartyMutation(
    formik.setErrors,
  );

  const handleLoadParties = useCallback(async (page: number, search?: string) => {
    const response = await api.v1.getPartiesMinimalV1ContractsPartiesMinimalGet({
      page,
      page_size: 20,
      search: search || undefined,
    });

    return response.data.data.map(({ id, name }) => ({ value: id, label: name }));
  }, []);

  const handleAddNewParty = useCallback(() => {
    setIsPartyModalOpen(true);
  }, []);

  const handlePartyCreated = useCallback(
    (party: PartyResponse) => {
      void formik.setFieldValue('party_id', party.id);
      void formik.setFieldValue('partyOption', { value: party.id, label: party.legal_name });
      setCacheUniq(Math.random());
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      title="Add party"
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[520px]' }}
    >
      <FormikProvider value={formik}>
        <div className="space-y-6">
          <SelectPaginate
            cacheUniqs={[cacheUniq]}
            label="Party"
            value={formik.values.partyOption || null}
            onChange={value => {
              const option = value as SelectPaginateOption<number> | null;
              void formik.setFieldValue('party_id', option?.value);
              void formik.setFieldValue('partyOption', option);
            }}
            onAsync={handleLoadParties}
            addedLastOption={{
              label: (
                <div className="flex items-center gap-2">
                  <img src={PlusIcon} alt="" className="h-4 w-4" />
                  <span>Add new Party</span>
                </div>
              ),
              onClick: handleAddNewParty,
            }}
            isClearable
            error={formik.touched.party_id ? (formik.errors.party_id as string) : undefined}
            placeholder="Select party"
          />

          <Select<PartyRole>
            label="Role"
            options={roleOptions}
            value={formik.values.role_type}
            onChange={value => formik.setFieldValue('role_type', value)}
            showBorderBottom
          />

          <div className="flex justify-end">
            <Button className="w-fit px-4" onClick={formik.submitForm} loading={isLoading}>
              Confirm
            </Button>
          </div>
        </div>
      </FormikProvider>

      <PartyModal
        open={isPartyModalOpen}
        onClose={() => setIsPartyModalOpen(false)}
        onSuccess={party => {
          handlePartyCreated(party);
          setIsPartyModalOpen(false);
        }}
      />
    </ModalContentWrapper>
  );
};

export default ContractPartyModal;
